import { z } from 'zod';

/** Sujet du canal de données réservé au chat ; tout autre sujet est ignoré (pas de réactions). */
export const CHAT_TOPIC = 'apovisio.chat.v1';
export const MAX_CHAT_LENGTH = 1000;
const MAX_PACKET_BYTES = 4096;

const chatPacketSchema = z
  .object({
    v: z.literal(1),
    id: z.string().regex(/^[A-Za-z0-9_-]{8,64}$/),
    text: z.string().trim().min(1).max(MAX_CHAT_LENGTH),
  })
  .strict();
export type ChatPacket = z.infer<typeof chatPacketSchema>;

const encoder = new TextEncoder();
const decoder = new TextDecoder('utf-8', { fatal: true });

export function encodeChat(text: string, id: string = crypto.randomUUID()): Uint8Array {
  const packet: ChatPacket = { v: 1, id: id.replace(/[^A-Za-z0-9_-]/g, ''), text: text.trim() };
  return encoder.encode(JSON.stringify(chatPacketSchema.parse(packet)));
}

/** Décodage strict : taille, UTF-8, JSON et schéma ; tout écart est rejeté silencieusement. */
export function decodeChat(payload: Uint8Array): ChatPacket | null {
  if (payload.byteLength === 0 || payload.byteLength > MAX_PACKET_BYTES) return null;
  try {
    const parsed = chatPacketSchema.safeParse(JSON.parse(decoder.decode(payload)));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/** Fenêtre glissante : au plus `limit` événements par `windowMs` et par clé. */
export class RateLimiter {
  private readonly history = new Map<string, number[]>();

  constructor(
    private readonly limit: number,
    private readonly windowMs: number,
  ) {}

  allow(key: string, now = Date.now()): boolean {
    const recent = (this.history.get(key) ?? []).filter((time) => now - time < this.windowMs);
    if (recent.length >= this.limit) {
      this.history.set(key, recent);
      return false;
    }
    recent.push(now);
    this.history.set(key, recent);
    return true;
  }
}
